import React, { useState } from 'react';
import { toast } from 'sonner';
import { Lock, Loader2 } from 'lucide-react';
import axiosInstance from '../lib/axios';
import { useAuthStore } from '../store/authStore';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * T-193 — Permite al admin cambiar la contraseña de su propio usuario desde el
 * panel de administración. Pide la contraseña actual como verificación de
 * identidad y confirma la nueva antes de enviarla al backend.
 *
 * Si el backend rota el token (200 con access_token/token en el body), se
 * persiste exactamente donde el login guarda el suyo: localStorage 'token' +
 * estado del authStore. Si la respuesta no trae token, no se hace nada extra.
 */
const ChangePasswordCard = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const resetFields = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validación client-side: la nueva no puede estar vacía y debe coincidir con
    // su confirmación. Si falla, error inline SIN llamar al backend.
    if (!newPassword) {
      setError('La nueva contraseña no puede estar vacía.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('La nueva contraseña y su confirmación no coinciden.');
      return;
    }

    setSaving(true);
    try {
      const res = await axiosInstance.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      // Respuesta tolerante: si el backend rota el token, lo persistimos donde
      // el login guarda el suyo (localStorage 'token' + estado de authStore).
      const newToken = res.data?.access_token || res.data?.token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        useAuthStore.setState({ token: newToken });
      }

      toast.success('Contraseña actualizada correctamente.');
      resetFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass rounded-xl p-6 space-y-3">
      <label className="text-sm font-medium flex items-center gap-2">
        <Lock className="w-4 h-4 text-purple-600" /> Cambiar mi contraseña
      </label>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Verifica tu identidad con la contraseña actual antes de establecer una nueva.
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="change-password-current">
            Contraseña actual
          </label>
          <Input
            id="change-password-current"
            data-testid="change-password-current"
            type="password"
            placeholder="••••••••"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="change-password-new">
            Nueva contraseña
          </label>
          <Input
            id="change-password-new"
            data-testid="change-password-new"
            type="password"
            placeholder="••••••••"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="change-password-confirm">
            Confirmar nueva contraseña
          </label>
          <Input
            id="change-password-confirm"
            data-testid="change-password-confirm"
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        <Button
          type="submit"
          data-testid="change-password-submit"
          disabled={saving}
          className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Cambiar contraseña
        </Button>
      </form>
    </div>
  );
};

export default ChangePasswordCard;
